import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Modal, Typography } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import * as Sentry from "@sentry/react";
import type { Story } from "@theirstory/common/api_types/backend";

import useMixPanel from "../../lib/mixpanel";
import { loadStoryFiles } from "../../store/actions/filesUpload";
import { notifyError, notifySuccess } from "../../store/actions/notify";
import { loadPortals, setPortalCollectionContent, storyToPortalContentItem } from "../../store/actions/portals";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { loadStoryForPortalContent } from "../../store/state/stories/actions";
import {
  DEFAULT_INDEX_SYSTEM_PROMPT,
  DEFAULT_INDEX_USER_PROMPT,
  isStoryEligibleForAIGeneration,
  requestIndexGeneration,
  requestSummaryGeneration,
} from "../Project/Modals/aiGenerationUtils";

import { BulkStoryContentList } from "./BulkStoryContentList";
import { PortalSelectionFields, type SupplementalFilesMode } from "./PortalSelectionFields";
import { getMissingRequirements, type GeneratedContentType } from "./portalStoryContentStatus";
import {
  mapPortalTasksWithLimit,
  PORTAL_AI_QUEUE_CONCURRENCY,
  PORTAL_AI_QUEUE_DELAY_MS,
  PORTAL_STORY_POLL_CONCURRENCY,
  PORTAL_STORY_POLL_DELAY_MS,
  PORTAL_SUPPLEMENTAL_FILES_CONCURRENCY,
} from "./portalTaskQueue";
import { SingleStoryRequirements } from "./SingleStoryRequirements";

type BulkGenerationItem = {
  story: Story;
  type: GeneratedContentType;
};

type SupplementalFile = {
  _id: string;
  release_form: boolean;
};

interface AddStoryToPortalModalProps {
  open: boolean;
  storyIds: string[];
  onClose: () => void;
}

const getSupplementalFileIds = (files: SupplementalFile[], mode: SupplementalFilesMode) => {
  if (mode === "none") return [];

  return files
    .filter((file) => mode === "all" || !file.release_form)
    .map((file) => file._id)
    .filter(Boolean);
};

export const AddStoryToPortalModal = ({ open, storyIds, onClose }: AddStoryToPortalModalProps) => {
  const dispatch = useAppDispatch();
  const isBulk = storyIds.length > 1;

  /**
   * selectors
   */
  const portals = useAppSelector((state) => state.portals.portals);
  const isLoadingPortals = useAppSelector((state) => state.portals.isLoadingPortals);
  const storiesFromList = useAppSelector((state) => state.stories.stories);
  const hubStory = useAppSelector((state) => state.storyAuthoring.story as Story | undefined);

  /**
   * state
   */
  const [storiesById, setStoriesById] = useState<Record<string, Story>>({});
  const [portalId, setPortalId] = useState("");
  const [collectionId, setCollectionId] = useState("");
  const [supplementalFilesMode, setSupplementalFilesMode] = useState<SupplementalFilesMode>("excludeReleaseForms");
  const [isSaving, setIsSaving] = useState(false);
  const requestedIdsRef = useRef(new Set<string>());
  const frozenAlreadyInCollectionIdsRef = useRef(new Set<string>());

  /**
   * variables
   */
  const portalsWithCollections = useMemo(() => portals.filter((portal) => portal.collections.length > 0), [portals]);
  const selectedPortal = portals.find((portal) => portal._id === portalId);
  const selectedCollection = selectedPortal?.collections.find((collection) => collection._id === collectionId);
  const loadedStories = storyIds.map((id) => storiesById[id]).filter((story): story is Story => Boolean(story));
  const liveAlreadyInCollectionIds = useMemo(
    () =>
      new Set(
        storyIds.filter((id) => selectedCollection?.content.some((item) => item.type === "story" && item._id === id))
      ),
    [storyIds, selectedCollection]
  );
  // setPortalCollectionContent updates the collection content optimistically before the save
  // request resolves, so while saving we keep showing the pre-save membership instead of
  // flashing "Already in collection" for the stories currently being added.
  if (!isSaving) {
    frozenAlreadyInCollectionIdsRef.current = liveAlreadyInCollectionIds;
  }
  const alreadyInCollectionIds = isSaving ? frozenAlreadyInCollectionIdsRef.current : liveAlreadyInCollectionIds;
  const storiesToAdd = loadedStories.filter((story) => !alreadyInCollectionIds.has(story._id));
  const addableStoriesToAdd = storiesToAdd.filter(
    (story) => getMissingRequirements(story).length === 0 || isStoryEligibleForAIGeneration(story)
  );
  const isLoadingStories = loadedStories.length < storyIds.length;
  const hasSingleStoryMissingRequirements =
    !isBulk && Boolean(loadedStories[0]) && getMissingRequirements(loadedStories[0]).length > 0;
  const canGenerateAI = Boolean(loadedStories[0] && isStoryEligibleForAIGeneration(loadedStories[0]));
  const alreadyInCollection = !isBulk && alreadyInCollectionIds.has(storyIds[0]);
  const isAddButtonLoading = isSaving || isLoadingStories || (isLoadingPortals && portalsWithCollections.length === 0);
  const hasMissingRequirements = storiesToAdd.some((story) => getMissingRequirements(story).length > 0);
  const canAdd =
    Boolean(selectedPortal && selectedCollection) && !isLoadingStories && addableStoriesToAdd.length > 0 && !isSaving;

  /**
   * effects
   */
  useEffect(() => {
    if (!open) return;

    dispatch(loadPortals());
  }, [dispatch, open]);

  useEffect(() => {
    if (!open) return;

    const idsToFetch: string[] = [];

    storyIds.forEach((id) => {
      const existingStory = (hubStory?._id === id ? hubStory : undefined) || storiesFromList.find((s) => s._id === id);
      if (existingStory) {
        setStoriesById((prev) => (prev[id] === existingStory ? prev : { ...prev, [id]: existingStory }));
        return;
      }

      if (requestedIdsRef.current.has(id)) return;
      requestedIdsRef.current.add(id);
      idsToFetch.push(id);
    });

    if (idsToFetch.length === 0) return;

    void mapPortalTasksWithLimit(
      idsToFetch,
      async (id) => {
        const loadedStory = await dispatch(loadStoryForPortalContent(id));
        setStoriesById((prev) => ({ ...prev, [id]: loadedStory }));
      },
      { concurrency: PORTAL_STORY_POLL_CONCURRENCY, delayMs: PORTAL_STORY_POLL_DELAY_MS }
    );
    // storyIds is intentionally compared by content, not identity, since callers may pass a
    // newly-created array on every render without the underlying selection having changed.
  }, [dispatch, hubStory, open, storiesFromList, storyIds.join(",")]);

  useEffect(() => {
    if (!open || portalId || portalsWithCollections.length === 0) return;

    const firstPortal = portalsWithCollections[0];
    setPortalId(firstPortal._id);
    setCollectionId(firstPortal.collections[0]?._id || "");
  }, [open, portalId, portalsWithCollections]);

  /**
   * helpers
   */
  const resetModal = () => {
    setPortalId("");
    setCollectionId("");
    setSupplementalFilesMode("excludeReleaseForms");
    setStoriesById({});
    requestedIdsRef.current.clear();
  };

  const handleClose = () => {
    if (isSaving) return;
    resetModal();
    onClose();
  };

  const handlePortalChange = (event: SelectChangeEvent<string>) => {
    const nextPortalId = event.target.value;
    const nextPortal = portals.find((portal) => portal._id === nextPortalId);
    setPortalId(nextPortalId);
    setCollectionId(nextPortal?.collections[0]?._id || "");
  };

  const loadStorySupplementalFileIds = async (storyId: string) => {
    if (supplementalFilesMode === "none") return [];

    const files = await dispatch(loadStoryFiles(storyId));
    return getSupplementalFileIds(files, supplementalFilesMode);
  };

  const requestAIContentGeneration = async (story: Story, type: GeneratedContentType) => {
    if (type === "description") {
      await requestSummaryGeneration(story._id);
    } else {
      await requestIndexGeneration(story._id, {
        system: DEFAULT_INDEX_SYSTEM_PROMPT,
        user: DEFAULT_INDEX_USER_PROMPT,
      });
    }
    useMixPanel("generate_portal_ai_content", {
      content_type: type,
      story_id: story._id,
    });
  };

  const handleAdd = async () => {
    if (!selectedPortal || !selectedCollection || !canAdd) return;

    setIsSaving(true);
    try {
      const generationItems = addableStoriesToAdd.reduce<BulkGenerationItem[]>((items, story) => {
        if (!isStoryEligibleForAIGeneration(story)) return items;
        if (!story.description?.trim()) items.push({ story, type: "description" });
        if (!story.indexes?.length) items.push({ story, type: "index" });
        return items;
      }, []);

      if (generationItems.length > 0) {
        await mapPortalTasksWithLimit(
          generationItems,
          async ({ story, type }) => {
            try {
              await requestAIContentGeneration(story, type);
            } catch (error) {
              Sentry.captureException(error, {
                tags: {
                  feature: "portals",
                  method: "autoGenerateMissingPortalContent",
                  storyId: story._id,
                  type,
                },
              });
            }
          },
          { concurrency: PORTAL_AI_QUEUE_CONCURRENCY, delayMs: PORTAL_AI_QUEUE_DELAY_MS }
        );
      }

      const additions = await mapPortalTasksWithLimit(
        addableStoriesToAdd,
        async (story) => {
          const supplementalFileIds = await loadStorySupplementalFileIds(story._id);
          const portalContentItem = storyToPortalContentItem(story);
          return supplementalFileIds.length > 0 ? { ...portalContentItem, supplementalFileIds } : portalContentItem;
        },
        { concurrency: PORTAL_SUPPLEMENTAL_FILES_CONCURRENCY }
      );
      const nextContent = [...selectedCollection.content, ...additions];

      await dispatch(setPortalCollectionContent(selectedPortal._id, selectedCollection._id, nextContent));
      useMixPanel("add_story_to_portal", {
        collection_id: selectedCollection._id,
        portal_id: selectedPortal._id,
        story_ids: addableStoriesToAdd.map((story) => story._id),
      });
      const skippedCount = storiesToAdd.length - addableStoriesToAdd.length;
      const addedMessage = addableStoriesToAdd.length > 1 ? "Recordings added to portal." : "Story added to portal.";
      dispatch(
        notifySuccess(skippedCount > 0 ? `${addedMessage} ${skippedCount} skipped (missing transcript).` : addedMessage)
      );
      resetModal();
      onClose();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "portals", method: "addStoryToPortal", storyIds: storyIds.join(",") },
      });
      dispatch(notifyError("There was an error adding to the portal."));
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * render
   */
  return (
    <Modal
      open={open}
      onClose={handleClose}
      onClick={(event) => event.stopPropagation()}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "4px",
          boxShadow: "0px 9px 46px 8px rgba(0,0,0,0.12)",
          width: isBulk ? 640 : 520,
          maxWidth: "calc(100vw - 32px)",
          outline: "none",
        }}
      >
        <Box sx={{ p: "24px" }}>
          <Typography sx={{ color: "#464C53", fontSize: "16px", lineHeight: "20px", fontWeight: 700 }}>
            Add to portal
          </Typography>
          <Typography sx={{ color: "#606971", fontSize: "13px", lineHeight: "20px", mt: "8px" }}>
            {isBulk
              ? "Choose a portal and collection for these interviews."
              : "Choose a portal and collection for this story."}
          </Typography>
          {hasMissingRequirements && (
            <Typography sx={{ color: "#606971", fontSize: "13px", lineHeight: "20px", mt: "8px" }}>
              {isBulk
                ? "Some of the interviews selected have no description or indexes. They will be automatically generated before adding them to the portal."
                : "This interview has no description or index. It will be automatically generated before adding it to the portal."}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", px: "24px", pb: "16px" }}>
          {isLoadingPortals && portalsWithCollections.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: "24px" }}>
              <CircularProgress size={24} />
            </Box>
          ) : portals.length === 0 ? (
            <Typography sx={{ color: "#606971", fontSize: "14px", lineHeight: "20px" }}>
              Create a portal and collection before adding stories.
            </Typography>
          ) : isLoadingStories ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: "24px" }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <PortalSelectionFields
                portals={portals}
                selectedPortal={selectedPortal}
                portalId={portalId}
                collectionId={collectionId}
                supplementalFilesMode={supplementalFilesMode}
                isSaving={isSaving}
                onPortalChange={handlePortalChange}
                onCollectionChange={(event) => setCollectionId(event.target.value)}
                onSupplementalFilesModeChange={setSupplementalFilesMode}
              />

              {isBulk ? (
                <BulkStoryContentList
                  storyIds={storyIds}
                  storiesById={storiesById}
                  alreadyInCollectionIds={alreadyInCollectionIds}
                  addableCount={addableStoriesToAdd.length}
                  skippedCount={storiesToAdd.length - addableStoriesToAdd.length}
                />
              ) : (
                <SingleStoryRequirements
                  hasMissingRequirements={hasSingleStoryMissingRequirements}
                  canGenerateAI={canGenerateAI}
                  alreadyInCollection={alreadyInCollection}
                  isSaving={isSaving}
                />
              )}
            </>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", px: "24px", py: "16px" }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isSaving}
            sx={{ borderColor: "#239B8B", color: "#239B8B", textTransform: "none", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={isAddButtonLoading || !canAdd}
            sx={{ backgroundColor: "#239B8B", minWidth: 112, textTransform: "none", fontWeight: 700 }}
          >
            {isAddButtonLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : isBulk ? (
              `Add ${addableStoriesToAdd.length} ${addableStoriesToAdd.length === 1 ? "interview" : "interviews"}`
            ) : (
              "Add to portal"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
